import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { ArrowLeft, ArrowRight, Loader2, Check } from "lucide-react";

const MAX_SELECT = 3;

export default function Assessment() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  // answers: { [questionId]: number[] }  (array of selected option indices, 1-3 items)
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/assessment/questions")
      .then(({ data }) => {
        setQuestions(data);
        setLoading(false);
      })
      .catch((e) => {
        setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] grid place-items-center">
          <Loader2 className="h-6 w-6 animate-spin text-ink-soft" />
        </div>
      </Layout>
    );
  }

  if (!questions.length) return <Layout><div className="p-10">{err || "No questions."}</div></Layout>;

  const q = questions[idx];
  const total = questions.length;
  const selected = answers[q.id] || [];
  const progress = ((idx + (selected.length > 0 ? 1 : 0)) / total) * 100;
  const canPrev = idx > 0;
  const canNext = selected.length > 0;
  const atCap = selected.length >= MAX_SELECT;
  const isLast = idx === total - 1;

  const toggle = (optionIdx) => {
    setAnswers((a) => {
      const curr = a[q.id] || [];
      if (curr.includes(optionIdx)) {
        return { ...a, [q.id]: curr.filter((i) => i !== optionIdx) };
      }
      if (curr.length >= MAX_SELECT) return a; // cap enforced
      return { ...a, [q.id]: [...curr, optionIdx] };
    });
  };

  const next = () => {
    if (!canNext) return;
    if (isLast) submit();
    else setIdx((i) => i + 1);
  };

  const prev = () => {
    if (canPrev) setIdx((i) => i - 1);
  };

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      const { data } = await api.post("/assessment/submit", { answers });
      await refresh();
      sessionStorage.setItem("cm_result", JSON.stringify(data));
      nav("/assessment/result", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div data-testid="assessment-page" className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="flex items-center justify-between text-xs uppercase tracking-widest font-semibold text-ink-soft mb-3">
          <span data-testid="assessment-progress-label">
            Question {idx + 1} of {total}
          </span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <div className="h-2 rounded-full bg-white border border-ink-soft overflow-hidden">
          <div
            data-testid="assessment-progress-bar"
            className="h-full bg-[var(--cm-primary)] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div key={q.id} className="mt-12 fade-up">
          <h1
            data-testid="assessment-question"
            className="font-display font-bold text-3xl md:text-4xl leading-tight tracking-tight max-w-2xl"
          >
            {q.text}
          </h1>
          <p className="mt-3 text-sm text-ink-soft flex items-center gap-2">
            <span>Pick up to <span className="font-semibold text-ink">3</span> that resonate.</span>
            <span
              data-testid="assessment-selection-count"
              className={`inline-flex items-center justify-center min-w-[3.25rem] rounded-full px-2 py-0.5 text-xs font-bold ${
                selected.length === 0
                  ? "bg-app text-ink-soft border border-ink-soft"
                  : atCap
                  ? "bg-[var(--cm-primary)] text-white"
                  : "bg-[var(--cm-secondary)]/15 text-[var(--cm-secondary)]"
              }`}
            >
              {selected.length}/{MAX_SELECT}
            </span>
          </p>

          <div className="mt-8 grid gap-3">
            {q.options.map((opt, i) => {
              const isSelected = selected.includes(i);
              const capBlock = !isSelected && atCap;
              return (
                <button
                  key={i}
                  data-testid={`assessment-option-${i}`}
                  data-selected={isSelected}
                  onClick={() => toggle(i)}
                  disabled={capBlock}
                  className={`text-left rounded-2xl px-5 py-4 border transition-all duration-200 ${
                    isSelected
                      ? "bg-[var(--cm-ink)] text-[var(--cm-surface)] border-[var(--cm-ink)]"
                      : capBlock
                      ? "bg-surface border-ink-soft opacity-40 cursor-not-allowed"
                      : "bg-surface border-ink-soft hover:border-[var(--cm-primary)] hover:-translate-y-0.5"
                  }`}
                >
                  <span className="flex items-start gap-3">
                    <span
                      className={`h-6 w-6 rounded-md border-2 mt-0.5 flex-shrink-0 grid place-items-center text-xs font-bold ${
                        isSelected
                          ? "bg-[var(--cm-primary)] border-[var(--cm-primary)] text-white"
                          : "border-ink-soft"
                      }`}
                    >
                      {isSelected ? <Check className="h-3.5 w-3.5" /> : String.fromCharCode(65 + i)}
                    </span>
                    <span className="font-medium">{opt.label}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {err && (
            <div data-testid="assessment-error" className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">
              {err}
            </div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button
              data-testid="assessment-prev-btn"
              onClick={prev}
              disabled={!canPrev}
              className="btn-outline inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-4 w-4" /> Previous
            </button>
            <button
              data-testid="assessment-next-btn"
              onClick={next}
              disabled={!canNext || submitting}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {submitting ? "Matching…" : isLast ? "See my match" : "Next"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
